import { useState } from "react";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import {
  Loader2, AlertTriangle, CheckCircle2, Scan, ShieldCheck, Send, ExternalLink,
  RotateCcw, Download, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Side-panel block that injects OCR + FIFA verify + Submit-to-FA controls into
 *  the mandate detail drawer. `mandate` is the full mandate object, `reload`
 *  re-fetches it after a successful action. */
export default function MandateComplianceActions({ mandate, reload, faRegistry, resendConfigured }) {
  const m = mandate;
  if (!m) return null;
  return (
    <div className="space-y-5">
      <OcrBlock mandate={m} reload={reload} />
      <FifaVerifyBlock mandate={m} reload={reload} />
      <SubmitFaBlock mandate={m} reload={reload} faRegistry={faRegistry} resendConfigured={resendConfigured} />
    </div>
  );
}

// ────────────────────────── OCR ──────────────────────────

function OcrBlock({ mandate, reload }) {
  const m = mandate;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const extract = m.ocr_extract;

  const run = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/compliance/mandates/${m.id}/ocr`);
      reload && reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "OCR failed");
    } finally {
      setBusy(false);
    }
  };

  const canRun = !!m.document_filename;

  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="compliance-ocr-block">
      <div className="flex items-center gap-2 mb-2">
        <Scan size={14} className="text-emerald-600" />
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">OCR · Claude vision</div>
      </div>
      {!canRun && (
        <div className="text-[12px] text-slate-500 mb-2">Upload a signed mandate document first to run OCR.</div>
      )}
      {extract ? (
        <>
          <div className="text-[12px] text-slate-500 mb-2">
            Extracted {fmtDateTime(m.ocr_run_at)} by {m.ocr_run_by_name || "—"} · {m.ocr_pages_processed || 1} page(s)
            {typeof extract.confidence_score === "number" && (
              <span className="ml-1">· confidence <b>{Math.round(extract.confidence_score * 100)}%</b></span>
            )}
          </div>
          <ExtractTable extract={extract} />
        </>
      ) : (
        <div className="text-[12px] text-slate-500 mb-2">
          OCR pulls partner agent, FIFA licence number, dates and scope straight from the document. Run it after uploading.
        </div>
      )}
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={run} disabled={!canRun || busy}
          className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="mandate-ocr-run-btn"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : extract ? <RotateCcw size={11} /> : <Scan size={11} />}
          {busy ? "Running…" : extract ? "Re-run OCR" : "Run OCR"}
        </button>
        {error && <div className="text-[11px] text-rose-700 flex items-center gap-1"><AlertTriangle size={10} /> {error}</div>}
      </div>
    </div>
  );
}

function ExtractTable({ extract }) {
  const ORDER = [
    "partner_agent_name", "partner_agent_fifa_license_number", "partner_agency_name",
    "player_full_name", "player_date_of_birth", "club_name",
    "start_date", "expiry_date", "scope_summary", "commission_percentage",
    "signatures_present", "language",
  ];
  const entries = ORDER.map((k) => [k, extract[k]]).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0 && extract.raw_response) {
    return (
      <pre className="text-[11px] text-slate-600 whitespace-pre-wrap bg-slate-50 rounded-lg p-2 max-h-40 overflow-auto" data-testid="ocr-extract">
        {extract.raw_response}
      </pre>
    );
  }
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 divide-y divide-slate-200" data-testid="ocr-extract">
      {entries.map(([k, v]) => (
        <div key={k} className="flex text-[12px] px-3 py-1.5">
          <div className="w-44 text-slate-500 capitalize">{k.replaceAll("_", " ")}</div>
          <div className="flex-1 font-bold text-slate-800 break-words">{String(v)}</div>
        </div>
      ))}
      {extract.warnings && extract.warnings.length > 0 && (
        <div className="px-3 py-1.5 text-[11px] text-amber-700 flex items-start gap-1.5">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <div>{extract.warnings.join(" · ")}</div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── FIFA TMS verify ──────────────────────────

function FifaVerifyBlock({ mandate, reload }) {
  const m = mandate;
  const [opening, setOpening] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(false);
  const [note, setNote] = useState("");
  const [openedUrl, setOpenedUrl] = useState(null);

  const openTms = async () => {
    setOpening(true); setError(null);
    try {
      const { data } = await api.get(`/compliance/mandates/${m.id}/fifa-tms-url`);
      window.open(data.url, "_blank", "noopener,noreferrer");
      setOpened(true);
      setOpenedUrl(data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not open FIFA registry");
    } finally {
      setOpening(false);
    }
  };

  const markVerified = async () => {
    setVerifying(true); setError(null);
    try {
      await api.post(`/compliance/mandates/${m.id}/verify-partner`, {
        verified_url: openedUrl,
        note: note || null,
      });
      reload && reload();
      setOpened(false);
      setNote("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save verification");
    } finally {
      setVerifying(false);
    }
  };

  const unverify = async () => {
    if (!confirm("Remove FIFA verification on this mandate?")) return;
    setVerifying(true); setError(null);
    try {
      await api.post(`/compliance/mandates/${m.id}/unverify-partner`);
      reload && reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not remove verification");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="compliance-fifa-block">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={14} className={m.fifa_verified ? "text-emerald-600" : "text-slate-400"} />
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">FIFA TMS verification</div>
        {m.fifa_verified && (
          <span className="pm-pill bg-emerald-50 text-emerald-800 border border-emerald-200 ml-auto" data-testid="fifa-verified-badge">
            <CheckCircle2 size={10} className="inline mr-1" /> Verified
          </span>
        )}
      </div>
      {m.fifa_verified ? (
        <div className="text-[12px] text-slate-600 space-y-1">
          <div>Verified {fmtDateTime(m.fifa_verified_at)} by <b>{m.fifa_verified_by_name || "—"}</b></div>
          {m.fifa_verified_note && <div className="text-slate-500">"{m.fifa_verified_note}"</div>}
          {m.fifa_verified_url && (
            <a href={m.fifa_verified_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline inline-flex items-center gap-1 text-[11px]">
              <ExternalLink size={10} /> Source URL
            </a>
          )}
          <button onClick={unverify} disabled={verifying} className="pm-btn-secondary h-7 px-2.5 rounded-lg text-[10px] font-bold mt-2" data-testid="mandate-unverify-btn">
            Remove verification
          </button>
        </div>
      ) : (
        <>
          <div className="text-[12px] text-slate-500 mb-2">
            We don't auto-scrape FIFA TMS (no public API). Open the registry, find the partner agent yourself, then mark verified — your name and timestamp are logged.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openTms} disabled={opening}
              className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5"
              data-testid="mandate-fifa-open-btn"
            >
              {opening ? <Loader2 size={11} className="animate-spin" /> : <ExternalLink size={11} />}
              Open FIFA registry
            </button>
            {opened && (
              <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={10} /> Opened in new tab</span>
            )}
          </div>
          {opened && (
            <div className="mt-3 space-y-2">
              <input
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Optional: licence number / note (e.g. 'IT-12345, active')"
                className="pm-input w-full text-[12px]"
                data-testid="mandate-fifa-note-input"
              />
              <button
                onClick={markVerified} disabled={verifying}
                className="pm-btn-primary h-8 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5"
                data-testid="mandate-fifa-verify-btn"
              >
                {verifying ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                Mark verified
              </button>
            </div>
          )}
        </>
      )}
      {error && <div className="text-[11px] text-rose-700 flex items-center gap-1 mt-2"><AlertTriangle size={10} /> {error}</div>}
    </div>
  );
}

// ────────────────────────── Submit to FA ──────────────────────────

function SubmitFaBlock({ mandate, reload, faRegistry, resendConfigured }) {
  const m = mandate;
  const [faCode, setFaCode] = useState((faRegistry && faRegistry[0]?.code) || "knvb");
  const [coverNote, setCoverNote] = useState("");
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post(`/compliance/mandates/${m.id}/submit-to-fa`, {
        fa_code: faCode,
        cc: ccList.length ? ccList : null,
        cover_note: coverNote || null,
      });
      reload && reload();
      setCoverNote("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("pm_token");
    const r = await fetch(`${API}/compliance/mandates/${m.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `mandate-${m.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const lastSub = m.fa_submission;
  const fa = (faRegistry || []).find((f) => f.code === faCode);

  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="compliance-fa-block">
      <div className="flex items-center gap-2 mb-2">
        <Send size={14} className="text-emerald-600" />
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">Submit to Football Association</div>
      </div>

      {lastSub && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3 text-[12px] text-emerald-900 flex items-start gap-2" data-testid="last-fa-submission">
          <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
          <div>
            Last filed with <b>{lastSub.fa_name}</b> ({lastSub.fa_email}) on {fmtDateTime(lastSub.submitted_at)} by {lastSub.submitted_by_name || "—"}
          </div>
        </div>
      )}

      {!resendConfigured && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mb-3 text-[12px] text-amber-900 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <div>
            Email gateway not configured. Paste a Resend API key in <code>backend/.env</code> as <code>RESEND_API_KEY</code> to enable one-click filing. You can still download the PDF and email it manually below.
          </div>
        </div>
      )}

      <div className="space-y-2">
        <select
          value={faCode} onChange={(e) => setFaCode(e.target.value)}
          className="pm-input w-full text-[12px]" data-testid="fa-select"
        >
          {(faRegistry || []).map((f) => (
            <option key={f.code} value={f.code}>{f.name} {f.email ? `· ${f.email}` : "· no email configured"}</option>
          ))}
        </select>
        <input
          value={cc} onChange={(e) => setCc(e.target.value)}
          placeholder="CC (comma-separated, optional)"
          className="pm-input w-full text-[12px]" data-testid="fa-cc-input"
        />
        <textarea
          value={coverNote} onChange={(e) => setCoverNote(e.target.value)}
          placeholder="Cover note for the FA (optional)"
          rows={2}
          className="pm-input w-full text-[12px]" data-testid="fa-cover-input"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={submit} disabled={busy || !fa?.email}
            className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="mandate-submit-fa-btn"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {busy ? "Sending…" : `Email PDF to ${fa?.name || "FA"}`}
          </button>
          <button
            onClick={downloadPdf}
            className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
            data-testid="mandate-download-pdf-btn"
          >
            <Download size={11} /> Download PDF
          </button>
        </div>
      </div>

      {error && <div className="text-[11px] text-rose-700 flex items-center gap-1 mt-2"><AlertTriangle size={10} /> {error}</div>}
    </div>
  );
}
